// src/components/MfaChallengeScreen.js
// Shown by App.js when user is signed in (AAL1) but has 2FA enrolled and
// hasn't completed the challenge yet. Blocks access to dashboard until verified.

import { useState, useEffect } from "react";
import PropTypes from 'prop-types';
import { useAuth } from "../hooks/AuthContext";
import { supabase } from "../config/supabase";

const CYAN  = "#22D3EE";
const BG    = "#0A1720";
const SURF  = "#0F2230";
const BORD  = "#142D3E";
const MUTED = "#7A8894";
const RED   = "#f87171";
const FONT  = "'Inter', -apple-system, sans-serif";
const MONO  = "Menlo, Monaco, Consolas, 'Courier New', monospace";

export function MfaChallengeScreen({ theme: T }) {
  const { completeMfaChallenge, signOut } = useAuth();
  const [factorId,  setFactorId]  = useState(null);
  const [code,      setCode]      = useState("");
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState("");
  const [finding,   setFinding]   = useState(true);

  // Find the verified TOTP factor on mount
  useEffect(() => {
    supabase.auth.mfa.listFactors().then(({ data }) => {
      const verified = (data?.totp || []).find(f => f.status === "verified");
      if (verified) setFactorId(verified.id);
      setFinding(false);
    }).catch(() => setFinding(false));
  }, []);

  async function handleVerify() {
    if (!code.trim() || code.length < 6 || !factorId) return;
    setLoading(true); setError("");
    try {
      await completeMfaChallenge(factorId, code);
      // mfaVerified is now true in AuthContext — App.js will render the dashboard
    } catch (err) {
      setError(
        err.message?.includes("Invalid TOTP") || err.message?.includes("invalid")
          ? "Incorrect code — check your authenticator app and try again."
          : err.message || "Verification failed. Please try again."
      );
    } finally {
      setLoading(false);
    }
  }

  if (finding) {
    return (
      <div style={{ minHeight: "100vh", background: BG, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ width: 40, height: 40, border: `3px solid ${BORD}`, borderTop: `3px solid ${CYAN}`, borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
        <style>{"@keyframes spin { to { transform: rotate(360deg); } }"}</style>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: T?.bg || BG, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 20, fontFamily: FONT, WebkitFontSmoothing: "antialiased" }}>

      {/* Logo */}
      <div style={{ marginBottom: 32, display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ width: 36, height: 36, background: CYAN, borderRadius: 9, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <span style={{ fontSize: 14, fontWeight: 900, color: "#0A1720" }}>EQ</span>
        </div>
        <span style={{ fontSize: 16, fontWeight: 900, color: "#fff", letterSpacing: "-0.02em" }}>EXIQ</span>
      </div>

      {/* Card */}
      <div style={{ background: T?.surface || SURF, border: `1px solid ${T?.border || BORD}`, borderRadius: 16, padding: 36, width: "100%", maxWidth: 400, textAlign: "center" }}>

        <div style={{ fontSize: 44, marginBottom: 16 }}></div>

        <div style={{ fontSize: 20, fontWeight: 900, color: "#fff", letterSpacing: "-0.02em", marginBottom: 8 }}>
          Two-Factor Authentication
        </div>
        <div style={{ fontSize: 14, color: "#B5B9BC", lineHeight: 1.7, marginBottom: 28 }}>
          Open your authenticator app and enter the 6-digit code for EXIQ.
        </div>

        {/* Code input */}
        <input
          type="text"
          inputMode="numeric"
          placeholder="000000"
          value={code}
          onChange={e => { setCode(e.target.value.replace(/\D/g, "").slice(0, 6)); setError(""); }}
          onKeyDown={e => e.key === "Enter" && handleVerify()}
          autoFocus
          style={{
            width: "100%", boxSizing: "border-box",
            background: T?.inputBg || "#142D3E",
            color: "#fff",
            border: `1px solid ${error ? RED : (T?.border || BORD)}`,
            borderRadius: 10, padding: "14px",
            fontSize: 28, fontWeight: 700,
            fontFamily: MONO,
            textAlign: "center",
            letterSpacing: "0.35em",
            outline: "none",
            marginBottom: 12,
            WebkitFontSmoothing: "antialiased",
          }}
        />

        {error && (
          <div style={{ background: `${RED}18`, border: `1px solid ${RED}44`, borderRadius: 8, padding: "10px 14px", fontSize: 13, color: RED, marginBottom: 12, textAlign: "left", lineHeight: 1.5 }}>
            {error}
          </div>
        )}

        <button
          onClick={handleVerify}
          disabled={loading || code.length < 6 || !factorId}
          style={{
            width: "100%", padding: "14px 0",
            background: (loading || code.length < 6 || !factorId) ? `${CYAN}55` : CYAN,
            color: "#0A1720", border: "none", borderRadius: 10,
            fontSize: 15, fontWeight: 700, fontFamily: FONT,
            cursor: (loading || code.length < 6 || !factorId) ? "not-allowed" : "pointer",
            marginBottom: 20,
          }}
        >
          {loading ? "Verifying..." : "Verify & Continue →"}
        </button>

        {/* Code hint */}
        <div style={{ fontSize: 12, color: "#B5B9BC", lineHeight: 1.6, marginBottom: 20 }}>
          Use Google Authenticator, Authy, 1Password, or any TOTP app. Codes refresh every 30 seconds.
        </div>

        {/* Sign out link */}
        <button
          onClick={() => signOut()}
          style={{ background: "none", border: "none", color: "#B5B9BC", fontSize: 13, cursor: "pointer", fontFamily: FONT, textDecoration: "underline" }}
        >
          Sign out and use a different account
        </button>
      </div>
    </div>
  );
}

MfaChallengeScreen.propTypes = {
  onSuccess: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
};
